import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { combineLatest, ReplaySubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { selectors, socialAppSelectors } from '../state/app';
import { getFeatureFlagObservable } from 'app/core/state';
import { LaunchDarklyFlags } from 'app/core/services/launch-darkly/launch-darkly.types';
import { SceneService } from '../services/scene.service';
import { AI_SUMMARIES } from 'entity/SocialSettings';

/**
 * recording icon in upper right corner
 */
@Component({
  selector: 'app-recording-banner',
  templateUrl: './recording-banner.component.html',
  styleUrls: ['./recording-banner.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RecordingBannerComponent implements OnInit, OnDestroy {
  private store = inject(Store);
  private changeDetectorRef = inject(ChangeDetectorRef);
  private sceneService = inject(SceneService);

  public isWideRecording = false;
  public isRecording = false;

  protected aiSummariesFF = false;
  public isAiSummariesRecording = false;
  public isPresentationActive = false;
  public inSocial = false;

  private destroyed$ = new ReplaySubject<boolean>(1);

  public constructor() {
    this.store
      .select(selectors.isPresentationRecording)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isRecording) => {
        this.isRecording = isRecording;
        this.changeDetectorRef.markForCheck();
      });

    this.store
      .select(selectors.isPresentationActive)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isPresentationActive) => {
        this.isPresentationActive = isPresentationActive;
        this.changeDetectorRef.markForCheck();
      });

    combineLatest([
      this.store.select(socialAppSelectors.isJoined),
      getFeatureFlagObservable(this.store, LaunchDarklyFlags.ai_summaries),
      this.store.select(socialAppSelectors.getRoomSettings),
    ])
      .pipe(takeUntil(this.destroyed$))
      .subscribe(([isJoined, aiSummariesFF]) => {
        this.inSocial = isJoined;
        this.aiSummariesFF = aiSummariesFF;
        const isiSummariesEnabled = this.sceneService.getSetting(AI_SUMMARIES);
        this.isAiSummariesRecording = !!isiSummariesEnabled;
        this.changeDetectorRef.markForCheck();
      });
  }

  public ngOnInit(): void {
    this.store
      .select(selectors.isWideRecording)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isWideRecording) => {
        this.isWideRecording = isWideRecording;
        this.changeDetectorRef.markForCheck();
      });
  }

  public get isRecordingActive(): boolean {
    return (!this.isAiSummariesRecording || this.isPresentationActive) && (this.isRecording || this.isWideRecording);
  }

  public get isAiSummariesRecordingActive(): boolean {
    return this.isAiSummariesRecording && !this.isPresentationActive && this.inSocial;
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
